import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, MessageCircle, MapPin, Calendar, Users, IndianRupee, Phone, Mail, ArrowRight, Sparkles, Star, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY } from '../mock';

export default function ThankYouPage() {
  const { state } = useLocation();
  const data = state || {};

  // Fallback if someone lands here directly without form data
  if (!data.name) {
    return <ThankYouFallback />;
  }

  const formatBudget = (b) => {
    if (!b) return 'Not specified';
    const map = {
      'under-10k': 'Under ₹10K',
      '10-20k': '₹10K – ₹20K',
      '20-35k': '₹20K – ₹35K',
      '35-60k': '₹35K – ₹60K',
      '60k+': '₹60K+',
      'under-10000': 'Under ₹10,000',
      '10000-15000': '₹10,000 – ₹15,000',
      '15000-25000': '₹15,000 – ₹25,000',
      '25000-40000': '₹25,000 – ₹40,000',
      '40000+': '₹40,000+',
    };
    return map[b] || b;
  };

  const whatsappText = encodeURIComponent(
    `Hi Mountains Chain Travel! 👋\n\nI just submitted an enquiry and would love to start planning my trip to ${data.destination || 'my dream destination'}.\n\nHere are my details:\nName: ${data.name}\nDestination: ${data.destination}\nTravel Date: ${data.when || data.date || 'Flexible'}\nTravelers: ${data.people || 'Not specified'}\nBudget: ${formatBudget(data.budget)}\n\nPlease share available options. Thank you!`
  );

  return (
    <>
      <Helmet>
        <title>Thank You - Mountains Chain Travel</title>
        <meta name="description" content="Your enquiry has been received. Our travel expert will reach out shortly." />
      </Helmet>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-brand-blueLight to-brand-blue pt-24 pb-20 lg:pt-32 lg:pb-28">
        {/* Decorative blobs */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-orange/15 blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-white/[0.06] blur-2xl" />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-brand-orange/10 blur-2xl" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          {/* Checkmark */}
          <div className="mx-auto mb-8 w-24 h-24 rounded-full bg-emerald-400/20 flex items-center justify-center animate-bounce-in">
            <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <CheckCircle2 size={44} className="text-white" strokeWidth={2.2} />
            </div>
          </div>

          <h1 className="font-poppins text-white text-[34px] lg:text-[48px] font-bold leading-[1.15]">
            Thank You,{' '}
            <span className="text-brand-orange">{data.name?.split(' ')[0] || 'Traveler'}</span>
            <span className="block text-[18px] lg:text-[22px] font-normal text-white/70 mt-3">
              Your enquiry has been received!
            </span>
          </h1>

          <p className="text-white/60 text-[15px] lg:text-[16px] mt-4 max-w-lg mx-auto leading-relaxed">
            Our travel designer is reviewing your preferences and will reach out on{' '}
            on <strong className="text-white/90">WhatsApp</strong> with a
            hand-crafted itinerary.
          </p>
        </div>
      </section>

      {/* ─── Summary Card ─── */}
      <section className="relative -mt-12 pb-16 lg:pb-20">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-premium border border-brand-greyDark overflow-hidden">
            {/* Card header */}
            <div className="bg-gradient-to-r from-brand-orange/10 to-brand-orange/5 px-8 py-5 border-b border-brand-greyDark">
              <div className="flex items-center gap-3">
                <Sparkles size={20} className="text-brand-orange" />
                <h2 className="font-poppins font-bold text-brand-blue text-[18px]">Your Trip Details</h2>
              </div>
              <p className="text-brand-blue/55 text-[13px] mt-1">A copy has been sent to our travel team</p>
            </div>

            {/* Card body */}
            <div className="px-8 py-6 space-y-0">
              <DetailRow icon={MapPin} label="Destination" value={data.destination} />
              <DetailRow icon={Calendar} label="Travel Date" value={data.when || data.date || 'Flexible'} />
              <DetailRow icon={Users} label="Travelers" value={data.people ? `${data.people} ${data.people === '1' ? 'Person' : 'People'}` : 'Not specified'} />
              <DetailRow icon={IndianRupee} label="Budget" value={formatBudget(data.budget)} />
              {data.email && <DetailRow icon={Mail} label="Email" value={data.email} />}
              <DetailRow icon={Phone} label="Phone" value={data.phone} last />
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${whatsappText}`}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-13 px-8 rounded-xl font-semibold text-[15px] btn-glow inline-flex items-center gap-2.5 shadow-glow-orange">
                <MessageCircle size={18} />
                Chat with Us on WhatsApp
                <ArrowRight size={16} />
              </Button>
            </a>
            <Link to="/" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full h-13 px-8 rounded-xl font-semibold text-[15px] border-brand-greyDark text-brand-blue hover:bg-brand-grey">
                Browse Trips
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Trust Badges ─── */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Star, label: '4.8 Google Rating', sub: '2000+ Reviews' },
              { icon: Users, label: '3+ Lakh Travelers', sub: 'Since 2015' },
              { icon: ShieldCheck, label: 'Govt Approved', sub: 'Ministry of Tourism' },
              { icon: MessageCircle, label: '24x7 Support', sub: 'Real Humans' },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-brand-greyDark p-4 text-center hover:shadow-card transition-shadow">
                  <Icon size={22} className="text-brand-orange mx-auto mb-2" />
                  <div className="font-poppins font-bold text-brand-blue text-[13px]">{t.label}</div>
                  <div className="text-brand-blue/55 text-[11.5px]">{t.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Popular Destinations ─── */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-poppins font-bold text-brand-blue text-[22px] mb-2">While You Wait - Get Inspired</h3>
          <p className="text-brand-blue/60 text-[14px] mb-8">Explore our most popular destinations</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: 'Shimla Manali', slug: '/himachal-tours' },
              { name: 'Kashmir', slug: '/destinations/kashmir' },
              { name: 'Leh Ladakh', slug: '/destinations/leh-ladakh' },
              { name: 'Goa', slug: '/destinations/goa' },
              { name: 'Kerala', slug: '/destinations/kerala' },
              { name: 'Dubai', slug: '/international/dubai' },
            ].map((d, i) => (
              <Link
                key={i}
                to={d.slug}
                className="px-4 py-3 rounded-xl bg-white border border-brand-greyDark text-brand-blue font-poppins font-semibold text-[13px] hover:border-brand-orange hover:text-brand-orange hover:-translate-y-0.5 transition-all"
              >
                {d.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ─── Detail Row ─── */
function DetailRow({ icon: Icon, label, value, last }) {
  return (
    <div className={`flex items-center gap-4 py-4 ${!last ? 'border-b border-brand-greyDark/60' : ''}`}>
      <div className="w-10 h-10 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-brand-blue/55 uppercase tracking-wider">{label}</div>
        <div className="font-poppins font-semibold text-brand-blue text-[15px] truncate">{value || 'Not specified'}</div>
      </div>
    </div>
  );
}

/* ─── Fallback if no data ─── */
function ThankYouFallback() {
  return (
    <>
      <Helmet>
        <title>Thank You - Mountains Chain Travel</title>
      </Helmet>
      <section className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-brand-blue via-brand-blueLight to-brand-blue">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-brand-orange/20 flex items-center justify-center mb-6">
            <MessageCircle size={40} className="text-brand-orange" />
          </div>
          <h1 className="font-poppins text-white text-[32px] font-bold leading-tight">
            We'd Love to Hear From You!
          </h1>
          <p className="text-white/60 mt-4 text-[15px] leading-relaxed">
            It looks like you landed here directly. Head over to our enquiry form to
            start planning your dream trip!
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/">
              <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-7 rounded-xl font-semibold">
                Browse Trips
              </Button>
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi! I would like to plan a trip with Mountains Chain Travel.')}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="outline" className="h-12 px-7 rounded-xl font-semibold border-white/30 text-white hover:bg-white/10">
                <MessageCircle size={16} className="mr-2" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
